/**
 * The top of a screen: breadcrumbs, the title, a line saying what the screen is for, facts about
 * it, and the actions that apply to the whole page. It also sets the browser tab's title to
 * "Title · Site", so history and the tab strip say where a person is.
 *
 * A long title stops at two lines, and then it takes a tab stop and shows its full text in a tip on
 * hover and on focus. Below 640px
 * the actions other than the `primary` one fold into a "More" menu, because a row of buttons does
 * not fit a phone, and the primary action stays a button in reach. Only one set of controls is ever
 * in the tab order. The `primary` action is the one main thing to do here, and there is at most one.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect } from "react";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { Button } from "./Button.tsx";
import { Link } from "./Link.tsx";
import type { Navigate } from "./Link.tsx";
import type { MenuItem } from "./Menu.tsx";
import { Menu } from "./Menu.tsx";
import { Truncate } from "./Truncate.tsx";
import { Heading, Text } from "./Text.tsx";
import { useMedia } from "./useMedia.ts";
export interface Crumb {
  readonly label: string;
  readonly href: string;
}
export interface PageAction {
  readonly label: string;
  readonly onSelect: () => void;
  /** The one main action of the screen. */
  readonly primary?: boolean;
  readonly disabled?: boolean;
  /** Why it is not available, in a few words. */
  readonly reason?: string;
}
export interface PageHeaderProps {
  readonly title: string;
  /** The product's name for the tab title: "Lattice admin". */
  readonly siteName: string;
  readonly description?: string | undefined;
  /** The screens above this one, nearest last. The current screen is the title, not a crumb. */
  readonly crumbs?: readonly Crumb[] | undefined;
  /** Facts about the screen, such as when it was last saved. A change in them is announced. */
  readonly meta?: ReactNode;
  readonly actions?: readonly PageAction[] | undefined;
  readonly navigate?: Navigate | undefined;
  readonly testId?: string;
}
function menuItem(action: PageAction): MenuItem {
  const item: MenuItem = {
    label: action.label,
    onSelect: action.onSelect,
    disabled: action.disabled === true
  };
  return action.reason === undefined ? item : {
    ...item,
    reason: action.reason
  };
}
function ActionButtons({
  actions
}: {
  readonly actions: readonly PageAction[];
}) {
  return <>
      {actions.map(action => <Button corners="rounded" key={action.label} variant={action.primary === true ? "primary" : "secondary"} onClick={action.onSelect} disabled={action.disabled === true} disabledReason={action.reason}>
          {action.label}
        </Button>)}
    </>;
}
export function PageHeader({
  title,
  siteName,
  description,
  crumbs = [],
  meta,
  actions = [],
  navigate,
  testId
}: PageHeaderProps) {
  const phone = useMedia("(max-width: 639px)");
  useEffect(() => {
    const before = document.title;
    document.title = `${title} · ${siteName}`;
    return () => {
      document.title = before;
    };
  }, [title, siteName]);
  return <header data-testid={testId} className="x78zum5 xdt5ytf x1173whf xeuugli">
      {crumbs.length === 0 ? null : <nav aria-label="Breadcrumb" className="x78zum5 x1a02dak x6s0dn4 x1173whf">
          {crumbs.map((crumb, at) => <span key={crumb.href} className="x78zum5 x1a02dak x6s0dn4 x1173whf">
              <Link href={crumb.href} variant="subtle" navigate={navigate}>
                {crumb.label}
              </Link>
              {at < crumbs.length - 1 ? <span aria-hidden="true">
                  <Text as="span" tone="tertiary" variant="caption">
                    /
                  </Text>
                </span> : null}
            </span>)}
        </nav>}
      <div className="x78zum5 x1a02dak x1cy8zhl x1qughib x1r0x45p">
        <div className="x1ol2t1i x78zum5 xdt5ytf xskm3c9 xeuugli">
          <Heading level={1}>
            <Truncate value={title} lines={2} />
          </Heading>
          {description === undefined ? null : <Text tone="secondary">{description}</Text>}
        </div>
        {actions.length === 0 ? null : phone ? <div className="x78zum5 x1a02dak x6s0dn4 x1173whf">
            <ActionButtons actions={actions.filter(action => action.primary === true)} />
            <Menu label="More actions" triggerContent="More" items={actions.filter(action => action.primary !== true).map(menuItem)} />
          </div> : <div className="x78zum5 x1a02dak x6s0dn4 x1173whf">
            <ActionButtons actions={actions} />
          </div>}
      </div>
      {meta === undefined ? null : <div role="status" className="x78zum5 x1a02dak x6s0dn4 x1vdzsm xcfm6fe">
          {meta}
        </div>}
    </header>;
}